import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Loader2, Save, Wand2 } from 'lucide-react';
import { HotFreshButtons } from './HotFreshButtons';
import { CategoryCards } from './CategoryCards';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const LG = { nba: 'NBA', nfl: 'NFL', cfb: 'College Football' };
const SIZE = { nba: '15–20 legs, smart lines (~7 in 10 hit) vs tonight\'s opponent', nfl: '8–16 legs, smart lines (~7 in 10 hit), opponent defense checked', cfb: 'a winner for every game today' };

const toLeg = (league, l) => (l.stat === 'dd'
  ? { league, bet_type: 'player_prop', player_name: l.player, selection: `${l.player} double-double`, line: 2, stat_target: 'dd', game_date: l.date, espn_event_id: l.event_id }
  : l.player
  ? { league, bet_type: 'player_prop', player_name: l.player, selection: `${l.player} ${l.text}`, line: l.mark, stat_target: l.stat, game_date: l.date, espn_event_id: l.event_id }
  : { league, bet_type: 'moneyline', selection: `${l.team} to win`, home_team: l.home_team, away_team: l.away_team, espn_event_id: l.event_id });

const Ticket = ({ t, i, league, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const copy = () => navigator.clipboard.writeText(t.text).then(() => toast.success('Ticket copied')).catch(() => toast.error('Copy failed'));
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/parlays`, { platform: 'fanduel', stake: t.stake, potential_payout: 0, source: 'ticket_builder', legs: t.legs.map((l) => toLeg(league, l)) });
      toast.success('Saved to your tickets — add the payout with the pencil once you place it');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };
  let n = 0;
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${i === 0 ? 'border-emerald-500/60 bg-emerald-950/20' : 'border-zinc-800 bg-zinc-950/60'}`} data-testid={`built-ticket-${i}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-black text-white">{t.title} · {t.legs.length} legs</p>
          <p className="text-[11px] text-zinc-400">{t.why}</p>
          <p className="text-[11px] text-zinc-300">Cost ${t.stake} · chance every leg hits <b className="text-amber-300" data-testid={`built-ticket-chance-${i}`}>{(t.chance_all_hit * 100).toFixed(1)}%</b></p>
        </div>
        <div className="flex gap-1.5">
          <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-[11px] font-bold text-zinc-200 hover:bg-zinc-800" data-testid={`built-ticket-copy-${i}`}><Copy className="w-3 h-3" />Copy</button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-emerald-500 disabled:opacity-50" data-testid={`built-ticket-save-${i}`}><Save className="w-3 h-3" />Track it</button>
        </div>
      </div>
      {t.games.map((g) => (
        <div key={g.game}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-sky-300">{g.game}</p>
          {g.legs.map((l) => { n += 1; return (
            <p key={`${l.player || l.team}-${l.stat}`} className="text-[13px] text-zinc-100 font-mono">
              {n}. {l.player ? `${l.player} — ${l.text}` : l.text} <span className="text-[10px] text-zinc-500">{Math.round(l.prob * 100)}%</span>
            </p>
          ); })}
        </div>
      ))}
    </div>
  );
};

// One tap → ChatGPT-style big tickets built from the app's own picks, ready to copy or track.
export default function TicketBuilder({ league }) {
  const [stake, setStake] = useState(25);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [win, setWin] = useState('all');
  const build = async () => {
    setBusy(true);
    try {
      const r = (await axios.get(`${API}/build-tickets/${league}`, { params: { stake, window: win } })).data;
      setRes(r);
      if (!r.tickets.length) toast.info(win === 'all' ? 'Not enough safe legs on the board yet — try again after picks update' : `Not enough legs in the ${win} games — try another time window`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <section className="rounded-xl border border-emerald-500/40 bg-emerald-950/10 p-3 space-y-3" data-testid={`ticket-builder-${league}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <p className="text-sm font-bold text-emerald-300">Build my {LG[league]} tickets</p>
          <p className="text-[11px] text-zinc-400">{SIZE[league]}. Uses opponent matchups, injuries and what your past tickets taught the app.</p>
        </div>
        {[25, 50, 100].map((v) => (
          <button key={v} type="button" onClick={() => setStake(v)} data-testid={`builder-stake-${v}-${league}`}
            className={`rounded-md px-2 py-1 text-xs font-bold ${stake === v ? 'bg-emerald-500 text-black' : 'border border-zinc-700 text-zinc-300'}`}>${v}</button>
        ))}
        <input type="number" min="1" value={stake} onChange={(e) => setStake(Number(e.target.value) || 0)} className="w-16 rounded-md border border-zinc-700 bg-zinc-950 px-1.5 py-1 text-xs text-white" data-testid={`builder-stake-input-${league}`} />
        <button type="button" onClick={build} disabled={busy || stake <= 0} data-testid={`builder-run-${league}`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-black text-black hover:bg-emerald-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}Build tickets
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1" data-testid={`builder-window-${league}`}>
        <span className="text-[11px] text-zinc-400 mr-1">Games:</span>
        {[['all', 'All day'], ['morning', 'Morning'], ['midday', 'Midday'], ['night', 'Night']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setWin(k)} data-testid={`builder-window-${k}-${league}`}
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold transition-colors ${win === k ? 'bg-amber-400 text-black' : 'border border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}>{label}</button>
        ))}
        <span className="text-[10px] text-zinc-500 ml-1">Morning before 12 PM PT · Midday 12–4 PM · Night 4 PM+</span>
      </div>
      <HotFreshButtons league={league} stake={stake} />
      {(league === 'nba' || league === 'nfl') && <CategoryCards league={league} stake={stake} />}
      {res?.nbc_skipped?.length > 0 && (
        <p className="text-[11px] text-orange-300" data-testid="builder-nbc-skipped">Left out for injury news (NBC Sports): {res.nbc_skipped.join(', ')}</p>
      )}
      {res?.tickets?.map((t, i) => <Ticket key={t.title} t={t} i={i} league={league} />)}
    </section>
  );
}
